import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, FileDown, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/report/${token}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Referto non disponibile o link scaduto.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const openPdf = () => {
    window.open(`${API}/public/report/${token}/pdf`, "_blank", "noopener,noreferrer");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-brand-azure" size={28} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-2xl border border-red-200 p-8 max-w-md text-center" data-testid="public-report-error">
        <AlertTriangle className="text-red-500 mx-auto mb-3" size={40} />
        <h1 className="text-xl font-extrabold text-brand-navy mb-2">Referto non disponibile</h1>
        <p className="text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );

  const p = data?.patient || {};
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto" data-testid="public-report-view">
        {/* Header */}
        <div className="bg-brand-navy text-white rounded-t-2xl p-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/70">PrimeVision Software</div>
            <h1 className="text-2xl font-extrabold mt-0.5">Referto visita oculistica</h1>
            <div className="text-xs text-white/80 mt-1 flex items-center gap-1.5">
              <ShieldCheck size={12} /> Link sicuro privato. Non condividere con terzi.
            </div>
          </div>
          <button
            data-testid="public-download-pdf"
            onClick={openPdf}
            className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2.5 rounded-lg flex items-center gap-2 text-sm"
          >
            <FileDown size={15} /> Scarica PDF
          </button>
        </div>

        {/* Body */}
        <div className="bg-white rounded-b-2xl border border-t-0 border-[#E2E8F0] p-6 space-y-5">
          <section>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Paziente</div>
            <div className="text-lg font-extrabold text-brand-navy">{p.name} {p.surname}</div>
            {p.birth && <div className="text-sm text-slate-600">Data di nascita: {p.birth}</div>}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Medico oculista</div>
              <div className="text-brand-navy font-semibold">{data.doctor_name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Data visita</div>
              <div className="text-brand-navy font-semibold">{(data.closed_at || "").slice(0, 10)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Tipo visita</div>
              <div className="text-brand-navy font-semibold">{(data.service || "").replace(/_/g, " ").toUpperCase()}</div>
            </div>
          </section>

          {data.diagnosi && (
            <section data-testid="public-diagnosi">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Diagnosi</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.diagnosi}</div>
            </section>
          )}
          {data.terapia && (
            <section data-testid="public-terapia">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Terapia</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.terapia}</div>
            </section>
          )}
          {data.behavioral_protocol && (
            <section data-testid="public-protocollo">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Protocollo suggerito</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.behavioral_protocol}</div>
            </section>
          )}
          {data.referto && (
            <section data-testid="public-referto">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Referto</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.referto}</div>
            </section>
          )}

          <div className="border-t border-slate-200 pt-4 text-[10px] text-slate-500 italic">
            Documento generato elettronicamente da PrimeVision Software. La firma del medico e i dati di iscrizione all'Ordine sono inclusi nel PDF scaricabile.
            Per qualsiasi chiarimento, contatta direttamente il tuo medico oculista.
          </div>
        </div>
      </div>
    </div>
  );
}
